import { BOT_CALLS_PER_PLAYER, SERVER_CALLS_PER_KEY } from './key-terms';

/**
 * Lent keys (owner's call 2026-10-30, reversing v0.81.0: "I do want lent keys … Lent keys should help server wide and
 * faction wide. Server wide as needed and faction wide only to share data between the users own faction.").
 * Plan: docs/work/lent-keys-plan.md. One copy for the server, the Discord bot, the website and the extension.
 *
 * - Opt-in only, off by default, and only for a player who agreed to the terms version that names it (LENT_KEYS_TERMS):
 *   until then the switch explains it and stays off; the server refuses it.
 * - Server-wide: PUBLIC Torn data only (other players, factions, companies, markets, Torn itself — `isPublicLendPath`),
 *   asked only when something needs data that's stale, least-used lent key first (apps/api/src/lent-keys.ts).
 * - Faction-wide: the lender's own faction's data, for that faction only — an extra key for the Discord bot's watch of
 *   their faction (apps/discord core/faction-keys.ts), which serves only that faction's members.
 * - Their share: at most `share` calls in any 60 seconds (1–27, default 10) for both together. The 60-a-minute promise is
 *   per PLAYER: the server's own uses of their keys (SERVER_CALLS_PER_KEY, 20) + the share + the Discord bot's
 *   (BOT_CALLS_PER_PLAYER, 13) stay within LENT_KEY_MAX_PER_MINUTE (60) — well under Torn's 100. So the share is at most
 *   60 − 20 − 13 = 27.
 * - Torn calls the key wrong/paused/disabled (KEY_GONE_CODES): the lending is dropped, the key wiped, the lender told.
 *   Torn's "too many requests" (5): the key rests an hour. Stopping deletes the lending at once; so does Delete my data.
 */

/** The terms version that names lending (the coordinator writes its table text). Below it: nothing is lent. */
export const LENT_KEYS_TERMS = 8;
/**
 * The terms version that lets the Discord bot answer PUBLIC look-ups (links by Discord id, /whois, faction names, giveaway
 * checks, the item list) for anyone in a Discord server with the bot: with a member's "Your faction" key, for their
 * faction's servers, and with a lent key, for any server. Below it a key is never used for those (the coordinator writes
 * the table text).
 */
export const BOT_PUBLIC_TERMS = 14;
export const LEND_SHARE_MIN = 1;
/** 60 − the server's own uses (20) − the bot's (13): the share never takes a player over 60 a minute with Omni-Torn. */
export const LEND_SHARE_MAX = 27;
export const LEND_SHARE_DEFAULT = 10;
/** Every server-side use of one lent key together (the server's own uses + lending + the Discord bot) stays under this. */
export const LENT_KEY_MAX_PER_MINUTE = 60;
/** A lent key Torn answered "too many requests" (or its daily limit) rests this long. */
export const LEND_REST_MS = 3_600_000;

/** A share as asked (anything) → a whole number 1–27. */
export function clampShare(n: unknown): number {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return LEND_SHARE_DEFAULT;
  return Math.max(LEND_SHARE_MIN, Math.min(LEND_SHARE_MAX, v));
}

/**
 * The server's per-key budget (apps/api/src/key-budget.ts) for a key lent with this share: its own uses
 * (SERVER_CALLS_PER_KEY) plus the share — never more than 60 less the Discord bot's share of the same player.
 */
export function lentServerBudget(share: number): number {
  return Math.min(SERVER_CALLS_PER_KEY + clampShare(share), LENT_KEY_MAX_PER_MINUTE - BOT_CALLS_PER_PLAYER);
}

/** What a lent key was used for (the lender's "what my key did"). */
export type LendUse = 'player-db' | 'player-search' | 'faction-roster' | 'targets' | 'npc' | 'public-pages' | 'faction-bot' | 'bot-public' | 'check';

export const LEND_USE_NAMES: Record<LendUse, string> = {
  'player-db': 'Player database (other players’ public profiles)',
  'player-search': 'Player search by name',
  'faction-roster': 'Who is in a faction (public member lists, wars, chains)',
  targets: 'Target finder checks',
  npc: 'NPC loot timers',
  'public-pages': 'Website pages (public Torn data)',
  'faction-bot': 'Your faction’s data, for your faction (Discord bot, website, extension)',
  'bot-public': 'Discord bot look-ups for servers without a key of their own (players’ public profiles, faction names, the item list)',
  check: 'Checking the key itself',
};

export const isLendUse = (x: unknown): x is LendUse => typeof x === 'string' && Object.prototype.hasOwnProperty.call(LEND_USE_NAMES, x);

// ---------------------------------------------------------------- what server-wide lending may read

const ID = '[1-9]\\d{0,10}';
/** Torn v2 paths of PUBLIC data about someone else (an id is always named: no id means "the key's owner"). */
const PUBLIC_PATHS: readonly RegExp[] = [
  new RegExp(`^/user/${ID}/(basic|profile|personalstats|bazaar|competition|discord|faction|hof|icons|job)$`),
  /^\/user\/search$/,
  new RegExp(`^/faction/${ID}/(basic|members|rankedwars|wars|chain|chains|hof|territory|raids|territorywars)$`),
  // (No company employees: asked of the lender's own company, Torn answers its private view.)
  new RegExp(`^/company/${ID}/profile$`),
  new RegExp(`^/market/${ID}/(itemmarket|bazaar|auctionhouse)$`),
  // Torn's finished auctions (the market pool's RW values, S31): public market data, no player's own.
  /^\/market\/(bazaar|auctionhouse)$/,
  /^\/torn\/(items|honors|medals|education|stocks|bank|cards|territory|rankedwars|rackets|hof|factionhof|organizedcrimes|calendar|itemammo|itemmods|merits|properties|logtypes|logcategories)$/,
  new RegExp(`^/torn/${ID}/(items|honors|medals|education|itemdetails|territory)$`),
  /^\/racing\/(cars|tracks|carupgrades|races)$/,
  new RegExp(`^/racing/${ID}/(race|records)$`),
];
/** Query parameters a public read may carry (never `key`, never `selections` — the selection is in the path). */
const PUBLIC_PARAMS = new Set(['limit', 'offset', 'sort', 'from', 'to', 'cat', 'stat', 'timestamp', 'striptags', 'name', 'ids', 'filters']);

/**
 * Whose data a public path is about — a lender whose own player id or faction this is must not answer it: Torn shows a key's
 * owner more of their OWN profile/faction (icons, personal stats, job, members' private fields) than of anyone else's.
 */
export function lendPathSubject(path: string): { user: number | null; faction: number | null } {
  const bare = path.replace(/[?#].*$/, '');
  const u = /^\/user\/(\d+)\//.exec(bare);
  const f = /^\/faction\/(\d+)\//.exec(bare);
  return { user: u ? Number(u[1]) : null, faction: f ? Number(f[1]) : null };
}

/** May this lender's key answer this path for others (never about their own player or faction)? */
export const lenderMayAsk = (path: string, lender: { playerId: number; factionId: number }): boolean => {
  const s = lendPathSubject(path);
  return s.user !== lender.playerId && !(s.faction !== null && lender.factionId > 0 && s.faction === lender.factionId);
};

/** A server-wide lent call reads only public data: the path (and its query, if any) must be one of these. */
export function isPublicLendPath(path: string, params: Record<string, unknown> = {}): boolean {
  if (typeof path !== 'string' || path.length > 200 || path.includes('#')) return false;
  const q = path.indexOf('?');
  const bare = q < 0 ? path : path.slice(0, q);
  if (/key/i.test(bare)) return false;
  if (!PUBLIC_PATHS.some((re) => re.test(bare))) return false;
  const names = [...(q < 0 ? [] : [...new URLSearchParams(path.slice(q + 1)).keys()]), ...Object.keys(params)];
  return names.every((n) => PUBLIC_PARAMS.has(n.toLowerCase()));
}

// ---------------------------------------------------------------- what the lender sees

export interface LendCalls {
  /** Calls by use in the last hour / the last day, most first. */
  hour: Array<{ use: LendUse; name: string; n: number }>;
  day: Array<{ use: LendUse; name: string; n: number }>;
  hourTotal: number;
  dayTotal: number;
}

/** GET/PUT /api/me/lend (website Account, extension options; Discord /account status shows a line of it). */
export interface LendStatus {
  /** Lending works on this server (its key store is there). */
  available: boolean;
  termsNeeded: number;
  /** Agreed to LENT_KEYS_TERMS or later: the switch may go on. */
  agreed: boolean;
  on: boolean;
  /** Calls a minute others may make with it (the share), 1–27. */
  share: number;
  /** Also for their own faction (the Discord bot's faction watch). */
  faction: boolean;
  /** 'account': the key Omni-Torn keeps for their account; 'separate': a key given just for lending. */
  source: 'account' | 'separate' | null;
  /** 'off': nothing lent; 'bad': Torn stopped taking the key, so it was removed (problem says why). */
  status: 'off' | 'ok' | 'resting' | 'bad';
  problem: string | null;
  restUntil: number | null;
  since: number | null;
  factionId: number | null;
  factionAccess: boolean;
  accessLevel: number | null;
  /** Omni-Torn keeps a working key for their account (what "use my account key" would lend). */
  hasAccountKey: boolean;
  calls: LendCalls;
}

/** In plain words, wherever the switch is (website, extension, Discord). */
export const LEND_WHAT =
  'Lend your key to Omni-Torn: within the share you pick (calls a minute), it reads PUBLIC Torn data for every Omni-Torn user — other players’ profiles, factions, companies, markets — when something needs it and what Omni-Torn has is out of date. With “my faction too”, the Discord bot also uses it to read your own faction’s data, for your faction only. Never your own private data for anyone else. Everything Omni-Torn does with your keys stays under 60 calls a minute (Torn allows 100). Stop any time: the lending is deleted at once.';

export const lendNeedsTermsText = (current: number): string =>
  current < LENT_KEYS_TERMS
    ? `Lending needs Omni-Torn’s terms version ${LENT_KEYS_TERMS}, which names it in the terms table. It comes with an update — until then this stays off.`
    : `Lending needs Omni-Torn’s terms version ${LENT_KEYS_TERMS} (the terms table names it): agree to the current terms first, then switch it on.`;
